import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

type Contest = {
  abbr: string;
  name: string;
  category: string;
  categoryBackground: string;
  bar: string;
  roles?: string[];
};

const CONTESTS: Contest[] = [
  {
    abbr: "PMGO",
    name: "Polícia Militar de Goiás",
    category: "SEGURANÇA PÚBLICA",
    categoryBackground: "var(--v3l-surface-navy-band)",
    bar: "var(--v3l-surface-navy-band)",
    roles: ["Soldado", "Cadete"],
  },
  {
    abbr: "CBMGO",
    name: "Corpo de Bombeiros Militar de Goiás",
    category: "SEGURANÇA PÚBLICA",
    categoryBackground: "var(--v3l-surface-navy-band)",
    bar: "var(--accent-amber-shape-on-light)",
    roles: ["Soldado", "Cadete"],
  },
  {
    abbr: "GCM",
    name: "Guarda Civil Municipal",
    category: "SEGURANÇA PÚBLICA",
    categoryBackground: "var(--v3l-surface-navy-band)",
    bar: "var(--v3l-surface-navy-band)",
  },
  {
    abbr: "Medicina UEG",
    name: "Vestibular de Medicina da UEG",
    category: "VESTIBULARES",
    categoryBackground: "var(--v3l-accent-teal)",
    bar: "var(--accent-amber-shape-on-light)",
  },
];

const LANDING_CONTESTS_CSS = `
.landing-contests,
.landing-contests *,
.landing-contests *::before,
.landing-contests *::after {
  box-sizing: border-box;
}

.landing-contests {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 100%;
  padding: 56px 16px;
  overflow-x: clip;
  background: var(--v3l-surface-base);
  color: var(--v3l-text-primary);
  font-family: inherit;
  text-align: left;
}

.landing-contests__header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
  width: 100%;
}

.landing-contests__eyebrow,
.landing-contests__title,
.landing-contests__subtitle,
.landing-contests__abbr,
.landing-contests__name {
  margin: 0;
  font-family: inherit;
}

.landing-contests__eyebrow {
  color: var(--v3l-text-primary);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 1.68px;
}

.landing-contests__title {
  color: var(--v3l-text-primary);
  font-size: 30px;
  font-weight: 700;
  line-height: 1.13;
  letter-spacing: -0.6px;
}

.landing-contests__subtitle {
  color: var(--v3l-text-secondary);
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
}

.landing-contests__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: start;
  gap: 14px;
  width: 100%;
}

.landing-contests__card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--v3l-border-divider);
  border-radius: 22px;
  background: var(--v3l-surface-base);
  color: var(--v3l-text-primary);
}

.landing-contests__bar {
  flex: none;
  width: 100%;
  height: 6px;
}

.landing-contests__body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  padding: 22px;
}

.landing-contests__category {
  align-self: flex-start;
  width: fit-content;
  max-width: 100%;
  padding: 4px 10px;
  border-radius: 999px;
  color: var(--v3l-band-navy-heading);
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.88px;
}

.landing-contests__abbr {
  color: var(--v3l-text-primary);
  font-size: 21px;
  font-weight: 700;
}

.landing-contests__name {
  color: var(--v3l-text-secondary);
  font-size: 15px;
  font-weight: 500;
  line-height: 1.45;
  overflow-wrap: break-word;
}

.landing-contests__roles {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-self: stretch;
  min-width: 0;
  max-width: 100%;
}

.landing-contests__role {
  max-width: 100%;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--v3l-surface-alt);
  color: var(--v3l-text-primary);
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
}

a.landing-contests__plan {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 4px;
  min-height: 44px;
  margin-top: auto;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--v3l-text-link);
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  text-decoration: underline;
  text-decoration-color: var(--v3l-text-link);
}

a.landing-contests__plan svg {
  flex: none;
}

a.landing-contests__plan:focus-visible {
  outline: 2px solid var(--v3l-ui-cta-focus-ring);
  outline-offset: 2px;
}

@media (min-width: 768px) {
  .landing-contests {
    gap: 40px;
    /* 120px matches the 1440 frame. Below that, the inset shrinks so a row of four still fits without splitting words. */
    padding: 100px max(16px, calc((100% - 1200px) / 2));
  }

  .landing-contests__title {
    font-size: 48px;
    line-height: 1.08;
    letter-spacing: -1.44px;
  }

  .landing-contests__subtitle {
    font-size: 18px;
    line-height: 1.53;
  }

  .landing-contests__grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    align-items: stretch;
    gap: 20px;
  }

  .landing-contests__card {
    height: 100%;
  }
}
`;

export default function LandingContests() {
  return (
    <section id="concursos" className="landing-contests" aria-label="Escolha seu concurso">
      <div className="landing-contests__header">
        <p className="landing-contests__eyebrow">CONCURSOS</p>
        <h2 className="landing-contests__title">Escolha seu concurso</h2>
        <p className="landing-contests__subtitle">
          Cada concurso tem o seu banco de questões e o seu plano.
        </p>
      </div>
      <div className="landing-contests__grid">
        {CONTESTS.map((contest) => (
          <article key={contest.abbr} className="landing-contests__card">
            <div className="landing-contests__bar" style={{ backgroundColor: contest.bar }} />
            <div className="landing-contests__body">
              <span
                className="landing-contests__category"
                style={{ backgroundColor: contest.categoryBackground }}
              >
                {contest.category}
              </span>
              <h3 className="landing-contests__abbr">{contest.abbr}</h3>
              <p className="landing-contests__name">{contest.name}</p>
              {contest.roles ? (
                <div className="landing-contests__roles">
                  {contest.roles.map((role) => (
                    <span key={role} className="landing-contests__role">
                      {role}
                    </span>
                  ))}
                </div>
              ) : null}
              <Link className="landing-contests__plan" to="/planos">
                Ver plano
                <ChevronRight aria-hidden="true" size={16} />
              </Link>
            </div>
          </article>
        ))}
      </div>
      <style>{LANDING_CONTESTS_CSS}</style>
    </section>
  );
}
